import { useEffect, useState, useMemo, useCallback } from "react";
import { api, API } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Download, Pencil } from "lucide-react";
import { VoiceMicButton, onVoiceExpenseSaved } from "@/components/VoiceExpense";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TransactionFilters } from "@/components/transactions/TransactionFilters";
import { TransactionFormModal } from "@/components/transactions/TransactionFormModal";

const emptyForm = () => ({
  type: "expense", amount: "", category: "Groceries", account_id: "", to_account_id: "",
  description: "", date: new Date().toISOString().slice(0, 10),
});

const emptyFilters = { q: "", cat: "", account: "", start: "", end: "", type: "all" };

function endOfDayISO(dateStr) {
  const e = new Date(dateStr);
  e.setHours(23, 59, 59, 999);
  return e.toISOString();
}

function filterParams(flt) {
  const params = new URLSearchParams();
  if (flt.q) params.set("q", flt.q);
  if (flt.cat) params.set("category", flt.cat);
  if (flt.account) params.set("account_id", flt.account);
  if (flt.start) params.set("start_date", new Date(flt.start).toISOString());
  if (flt.end) params.set("end_date", endOfDayISO(flt.end));
  if (flt.type !== "all") params.set("type", flt.type);
  return params;
}

const AMOUNT_STYLES = {
  income: { cls: "text-[#2E6F40]", sign: "+" },
  expense: { cls: "text-[#D94F3D]", sign: "−" },
  transfer: { cls: "text-[#6C7565]", sign: "⇄" },
  investment: { cls: "text-[#A67C00]", sign: "→" },
};

function TransactionRow({ txn: t, onEdit, onDelete }) {
  const style = AMOUNT_STYLES[t.type] || AMOUNT_STYLES.expense;
  return (
    <tr className="border-b border-[#EAE7DD] hover:bg-[#FDFBF7]">
      <td className="px-4 py-3 num text-[#6C7565]">{formatDate(t.date)}</td>
      <td className="px-4 py-3">{t.description || <span className="text-[#6C7565] italic">—</span>}</td>
      <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider">{t.category}</td>
      <td className={`px-4 py-3 text-right num font-medium ${style.cls}`}>
        {style.sign} {formatINR(t.amount)}
      </td>
      <td className="px-4 py-3 text-right">
        <span className="flex justify-end gap-3">
          <button data-testid={`edit-txn-${t.id}`} onClick={() => onEdit(t)} className="text-[#6C7565] hover:text-[#1A1F16]"><Pencil className="w-4 h-4" /></button>
          <button data-testid={`delete-txn-${t.id}`} onClick={() => onDelete(t.id)} className="text-[#6C7565] hover:text-[#D94F3D]"><Trash2 className="w-4 h-4" /></button>
        </span>
      </td>
    </tr>
  );
}

const TH = "px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]";

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [flt, setFlt] = useState(emptyFilters);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    const [t, a] = await Promise.all([
      api.get(`/transactions?${filterParams(flt).toString()}`),
      api.get("/accounts"),
    ]);
    setItems(t.data);
    setAccounts(a.data);
  }, [flt]);
  useEffect(() => {
    const id = setTimeout(load, 250); // debounce (covers typing in search)
    return () => clearTimeout(id);
  }, [load]);
  useEffect(() => onVoiceExpenseSaved(() => load()), [load]);

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      type: t.type, amount: String(t.amount), category: t.category,
      account_id: t.account_id || "", description: t.description || "",
      date: (t.date || "").slice(0, 10),
    });
    setOpen(true);
  };

  const closeModal = () => { setOpen(false); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      account_id: form.account_id || null,
      date: new Date(form.date).toISOString(),
    };
    try {
      if (editingId) {
        await api.put(`/transactions/${editingId}`, payload);
        toast.success("Transaction updated");
      } else {
        await api.post("/transactions", payload);
        toast.success("Transaction logged");
      }
      closeModal();
      load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = (id) => {
    const t = items.find((x) => x.id === id);
    setConfirm({
      title: "Delete transaction?",
      detail: `${(t?.type || "entry").replace(/^\w/, (c) => c.toUpperCase())} of ${formatINR(t?.amount || 0)} in ${t?.category || ""} (${formatDate(t?.date)}) will be permanently deleted.`,
      run: async () => { await api.delete(`/transactions/${id}`); toast.success("Removed"); load(); },
    });
  };

  const downloadCSV = () => {
    const params = new URLSearchParams();
    if (flt.start) params.set("start", new Date(flt.start).toISOString());
    if (flt.end) params.set("end", endOfDayISO(flt.end));
    fetch(`${API}/reports/transactions.csv?${params.toString()}`, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "finledger-transactions.csv";
        link.click();
        toast.success("CSV downloaded");
      })
      .catch(() => toast.error("Download failed"));
  };

  const totals = useMemo(() => {
    const income = items.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
    const expense = items.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
    return { income, expense, net: income - expense };
  }, [items]);

  const hasFilter = Boolean(flt.q || flt.start || flt.end || flt.cat || flt.account);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6 flex-wrap gap-4">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section II</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Transactions.</h2>
        </div>
        <div className="flex gap-3">
          <VoiceMicButton testid="txn-voice-btn" />
          <button
            data-testid="txn-download-csv"
            onClick={downloadCSV}
            className="border border-[#1A1F16] px-4 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] flex items-center gap-2"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button
            data-testid="add-transaction-btn"
            onClick={openNew}
            className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" /> New entry
          </button>
        </div>
      </div>

      <TransactionFilters
        flt={flt}
        onChange={setFlt}
        accounts={accounts}
        hasFilter={hasFilter}
        onClear={() => setFlt({ ...emptyFilters, type: flt.type })}
      />

      {/* Type filter + totals */}
      <div className="flex gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6C7565] border-b border-[#D8D4C7] pb-3 flex-wrap">
        {["all", "income", "expense", "transfer", "investment"].map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFlt({ ...flt, type: f })}
            className={`pb-1 border-b-2 ${flt.type === f ? "border-[#1A1F16] text-[#1A1F16]" : "border-transparent hover:text-[#1A1F16]"}`}
          >{f}</button>
        ))}
        <span className="ml-auto flex gap-4 items-center">
          <span data-testid="txn-count">{items.length} entries</span>
          <span className="text-[#2E6F40] num normal-case">↑ {formatINR(totals.income, { compact: true })}</span>
          <span className="text-[#D94F3D] num normal-case">↓ {formatINR(totals.expense, { compact: true })}</span>
        </span>
      </div>

      <div className="border border-[#D8D4C7] bg-white">
        <table className="w-full text-sm" data-testid="transactions-table">
          <thead>
            <tr className="border-b border-[#1A1F16] text-left">
              <th className={TH}>Date</th>
              <th className={TH}>Description</th>
              <th className={TH}>Category</th>
              <th className={`${TH} text-right`}>Amount</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[#6C7565]">No entries match your filters.</td></tr>
            ) : items.map((t) => (
              <TransactionRow key={t.id} txn={t} onEdit={openEdit} onDelete={remove} />
            ))}
          </tbody>
        </table>
      </div>

      <TransactionFormModal
        open={open}
        editingId={editingId}
        form={form}
        setForm={setForm}
        accounts={accounts}
        onSubmit={submit}
        onClose={closeModal}
      />
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
